<script setup lang="ts">
import { computed, PropType, ref } from "vue";
import {
    GroupChartRegenerationProblem,
    GroupChartSummary,
    Participant,
    ProductLanguages,
    Project,
} from "../../../types/PersologOnlineTypes.ts";
import { ProfileContractorAPI } from "../../../api";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";
import { i18n } from "../../../main.ts";
import { getDisplayName, getImpersonatedAccount } from "../../../utils/GeneralUtils.ts";
import { openPdf, openPdfInNewTab } from "../../../utils/PdfUtils.ts";
import { pickGenerationLanguage } from "../../../utils/LanguageUtils.ts";
import { groupChartToastGroup, JOB_PROFILE, JOB_PROFILE_SHORT } from "../../../utils/ProjectUtils.ts";

const props = defineProps({
    chart: {
        type: Object as PropType<GroupChartSummary>,
        required: true,
    },
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    languages: {
        type: Object as PropType<ProductLanguages | null>,
        default: null,
    },
    // Set by the parent once a chart whose PDFs were deleted is being created again.
    regenerationRequested: {
        type: Boolean,
        default: false,
    },
    feedbackRecipientRole: {
        type: String as PropType<string | undefined>,
        default: undefined,
    },
});

const emit = defineEmits<{
    (e: "deleted", groupChartId: number): void;
    (e: "changed", groupChartId: number): void;
    (e: "open:benchmark", groupChartId: number): void;
}>();

const { t } = useI18n();
const toast = useToast();
const toastGroup = groupChartToastGroup(props.project.id);

const regenerating = ref(false);
const deleting = ref(false);
const loading = ref(false);

const chartProduct = computed(() => props.chart.product ?? props.project.product);
// The PDFs went with a member's deleted data (RDT-475): nothing is generating, so no pending spinner.
const isPdfDeleted = computed(
    () => props.chart.pdfDeleted === true && props.chart.languages.length === 0 && !props.regenerationRequested,
);
const isPending = computed(() => props.chart.languages.length === 0 && !isPdfDeleted.value);
const PROBLEM_KEYS: Record<GroupChartRegenerationProblem, string> = {
    NO_PARTICIPANTS: "no-participants",
    FEEDBACK_RECIPIENT_MISSING: "feedback-recipient-missing",
    FEEDBACK_RECIPIENT_MULTIPLE: "feedback-recipient-multiple",
    FEEDBACK_GIVERS_MISSING: "feedback-givers-missing",
};
// Why the chart cannot be created again from the members left, e.g. a 361 chart without its feedback recipient.
const regenerationProblemText = computed(() => {
    const problem = props.chart.regenerationProblem;
    if (!problem) return null;
    return t(`project-details.group-chart.pdf-deleted.problem.${PROBLEM_KEYS[problem]}`, {
        role: props.feedbackRecipientRole ?? "",
    });
});
const impersonated = computed(() => getImpersonatedAccount() !== null);
const isBenchmarkSource = computed(
    () => props.project.product === JOB_PROFILE || props.project.product === JOB_PROFILE_SHORT,
);

const MAX_DISPLAYED_MEMBERS = 5;

const memberNames = computed(() =>
    props.chart.participantIds
        .map((id) => {
            const participant = props.participants.find((p) => p.id === id);
            return participant ? `${participant.firstName} ${participant.name}` : null;
        })
        .filter((name): name is string => name !== null),
);

// Show at most MAX_DISPLAYED_MEMBERS names inline; the full list stays in the tooltip.
const displayedMemberNames = computed(() => {
    const shown = memberNames.value.slice(0, MAX_DISPLAYED_MEMBERS).join(", ");
    return memberNames.value.length > MAX_DISPLAYED_MEMBERS ? `${shown} …` : shown;
});

const displayLanguage = (lang: string) => getDisplayName(i18n.global.locale.value, lang);

// Languages the chart has already been generated in (for view/download).
const viewItems = computed(() =>
    props.chart.languages.map((lang) => ({ label: displayLanguage(lang), command: () => viewPdf(lang) })),
);
const downloadItems = computed(() =>
    props.chart.languages.map((lang) => ({ label: displayLanguage(lang), command: () => downloadPdf(lang) })),
);

// All languages the product is available in (for generating the chart in an additional language).
const productLanguages = computed(() => props.languages?.[chartProduct.value] ?? props.chart.languages);
const regenerateItems = computed(() =>
    productLanguages.value.map((lang) => ({ label: displayLanguage(lang), command: () => regenerate(lang) })),
);
// The button itself (not its menu): the project language only if the product is active in it (RDT-532).
const defaultLanguage = computed(() => pickGenerationLanguage(productLanguages.value, props.project.language));

async function fetchBlob(language: string): Promise<Blob> {
    return ProfileContractorAPI.downloadPdf(chartProduct.value, props.chart.groupChartId, language);
}

/** Opens the PDF in the in-app viewer (same viewer used for participant profiles). */
async function viewPdf(language: string) {
    if (impersonated.value) return;
    loading.value = true;
    try {
        const blob = await fetchBlob(language);
        await openPdfInNewTab(blob, `${props.chart.label}-${language}`);
    } catch (error) {
        console.error("Error displaying group chart PDF:", error);
    } finally {
        loading.value = false;
    }
}

/** Downloads the PDF directly (same as the participant download action). */
async function downloadPdf(language: string) {
    if (impersonated.value) return;
    loading.value = true;
    try {
        const blob = await fetchBlob(language);
        openPdf(URL.createObjectURL(blob), `${props.chart.label}-${language}`, props.chart.groupChartId, false);
    } catch (error) {
        console.error("Error downloading group chart PDF:", error);
    } finally {
        loading.value = false;
    }
}

async function regenerate(language: string) {
    regenerating.value = true;
    try {
        await ProfileContractorAPI.regenerateGroupChartById(chartProduct.value, props.chart.groupChartId, language);
        emit("changed", props.chart.groupChartId);
        toast.add({
            group: toastGroup,
            severity: "success",
            summary: t("project-details.group-chart.toasts.heading-generation"),
            detail: t("project-details.group-chart.toasts.success", { language: displayLanguage(language) }),
            life: 8000,
        });
    } catch (error) {
        console.error("Error regenerating group chart:", error);
        toast.add({
            group: toastGroup,
            severity: "error",
            summary: t("project-details.group-chart.toasts.heading-generation"),
            detail: t("project-details.participants.regenerate.error", {
                firstname: props.chart.label,
                lastname: props.project?.name,
                language: displayLanguage(language),
            }),
            life: 8000,
        });
    } finally {
        regenerating.value = false;
    }
}

async function deleteChart() {
    deleting.value = true;
    try {
        await ProfileContractorAPI.deleteGroupChartById(props.chart.groupChartId);
        emit("deleted", props.chart.groupChartId);
        toast.add({
            group: toastGroup,
            severity: "success",
            summary: t("project-details.group-chart.heading"),
            detail: t("project-details.group-chart.toasts.deleted"),
            life: 5000,
        });
    } catch (error) {
        console.error("Error deleting group chart:", error);
        toast.add({
            group: toastGroup,
            severity: "error",
            summary: t("project-details.group-chart.heading"),
            detail: t("project-details.group-chart.toasts.delete-error"),
            life: 8000,
        });
    } finally {
        deleting.value = false;
    }
}
</script>

<template>
    <div class="flex flex-col gap-2 py-2 md:flex-row md:items-center md:justify-between md:gap-4">
        <div class="flex flex-col w-full md:flex-1 md:w-auto" style="min-width: 0">
            <span class="font-semibold">{{ chart.label }}</span>
            <span v-if="memberNames.length" class="text-sm text-content-muted" v-tooltip.top="memberNames.join(', ')">
                {{ t("project-details.group-chart.members", { count: memberNames.length }) }}: {{ displayedMemberNames }}
            </span>
            <span
                v-if="isPdfDeleted"
                :id="`group-chart_pdf-deleted-reason-${chart.groupChartId}`"
                class="text-sm text-content-muted"
            >
                {{ t("project-details.group-chart.pdf-deleted.reason") }}
            </span>
            <span
                v-if="isPdfDeleted && regenerationProblemText"
                :id="`group-chart_regeneration-problem-${chart.groupChartId}`"
                class="text-sm text-content-muted"
            >
                {{ regenerationProblemText }}
            </span>
        </div>

        <div class="flex items-center gap-2 shrink-0 w-full justify-end md:w-auto">
            <span
                v-if="isPending"
                :id="`group-chart_pending-${chart.groupChartId}`"
                class="text-sm text-content-muted flex items-center"
            >
                <i class="pi pi-spin pi-spinner mr-1" />
                {{ t("project-details.group-chart.status-pending") }}
            </span>

            <template v-else-if="isPdfDeleted">
                <span
                    :id="`group-chart_pdf-deleted-${chart.groupChartId}`"
                    class="text-sm text-content-muted flex items-center"
                >
                    <i class="pi pi-info-circle mr-1" />
                    {{ t("project-details.group-chart.pdf-deleted.status") }}
                </span>
                <SplitButton
                    v-if="!chart.regenerationProblem"
                    :id="`group-chart_regenerate-${chart.groupChartId}`"
                    :model="regenerateItems"
                    :aria-label="t('project-details.group-chart.pdf-deleted.regenerate')"
                    :pt="{ pcButton: { root: { class: 'bg-persolog-blue' } }, pcDropdown: { root: { class: 'bg-persolog-blue' } } }"
                    v-tooltip.top="{ value: t('project-details.group-chart.pdf-deleted.regenerate'), showDelay: 500 }"
                    @click="regenerate(defaultLanguage)"
                >
                    <i :class="regenerating ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'" />
                </SplitButton>
            </template>

            <template v-else>
                <SplitButton
                    v-if="viewItems.length > 1"
                    :id="`group-chart_view-${chart.groupChartId}`"
                    :disabled="impersonated"
                    :model="viewItems"
                    :pt="{ pcButton: { root: { class: 'bg-persolog-blue' } }, pcDropdown: { root: { class: 'bg-persolog-blue' } } }"
                    v-tooltip.top="{ value: t('project-details.participants.tooltips.view'), showDelay: 500 }"
                    @click="viewPdf(chart.languages[0])"
                >
                    <i :class="loading ? 'pi pi-spin pi-spinner' : 'pi pi-eye'" />
                </SplitButton>
                <Button
                    v-else
                    :id="`group-chart_view-${chart.groupChartId}`"
                    :disabled="impersonated"
                    class="bg-persolog-blue"
                    :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-eye'"
                    v-tooltip.top="{ value: t('project-details.participants.tooltips.view'), showDelay: 500 }"
                    @click="viewPdf(chart.languages[0])"
                />

                <SplitButton
                    v-if="downloadItems.length > 1"
                    :id="`group-chart_download-${chart.groupChartId}`"
                    :disabled="impersonated"
                    :model="downloadItems"
                    :pt="{ pcButton: { root: { class: 'bg-persolog-blue' } }, pcDropdown: { root: { class: 'bg-persolog-blue' } } }"
                    v-tooltip.top="{ value: t('project-details.participant.tooltips.download'), showDelay: 500 }"
                    @click="downloadPdf(chart.languages[0])"
                >
                    <i class="pi pi-download" />
                </SplitButton>
                <Button
                    v-else
                    :id="`group-chart_download-${chart.groupChartId}`"
                    :disabled="impersonated"
                    class="bg-persolog-blue"
                    icon="pi pi-download"
                    v-tooltip.top="{ value: t('project-details.participant.tooltips.download'), showDelay: 500 }"
                    @click="downloadPdf(chart.languages[0])"
                />

                <SplitButton
                    :id="`group-chart_regenerate-${chart.groupChartId}`"
                    :model="regenerateItems"
                    aria-label="Generate language"
                    :pt="{ pcButton: { root: { class: 'bg-persolog-blue' } }, pcDropdown: { root: { class: 'bg-persolog-blue' } } }"
                    v-tooltip.top="{ value: t('project-details.group-chart.regenerate'), showDelay: 500 }"
                    @click="regenerate(defaultLanguage)"
                >
                    <i :class="regenerating ? 'pi pi-spin pi-spinner' : 'pi pi-language'" />
                </SplitButton>

                <Button
                    v-if="isBenchmarkSource"
                    :id="`group-chart_benchmark-${chart.groupChartId}`"
                    class="bg-persolog-blue"
                    icon="pi pi-file-import"
                    v-tooltip.top="t('project-details.group-chart.import-to-benchmark')"
                    @click="emit('open:benchmark', chart.groupChartId)"
                />
            </template>

            <Button
                :id="`group-chart_delete-${chart.groupChartId}`"
                severity="danger"
                icon="pi pi-trash"
                outlined
                aria-label="Delete"
                :loading="deleting"
                v-tooltip.top="t('project-details.group-chart.delete')"
                @click="deleteChart"
            />
        </div>
    </div>
</template>
